/**
 * Special Abilities System for Bean Wars
 * Each character has a unique special ability activated with the E key or double-tap joystick
 */

import { Bullet } from './Bullet.js';
import { createExplosion } from './Particle.js';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '../config/constants.js';

/**
 * Special Ability Registry
 * Maps special ability IDs to their implementations
 */
export const SPECIAL_ABILITIES = {
  // === CLOAKS ABILITIES ===

  /**
   * Blood Fan Barrage (Bat - c_151)
   * For 5 seconds, every shot becomes a 5-way red laser spread
   */
  blood_fan: {
    duration: 5000,
    activate(player, gameEngine) {
      this.originalFire = player.fire.bind(player);
      player.fire = (currentTime) => {
        const centerBullet = this.originalFire(currentTime);
        const bullets = [];

        // Create 5-way spread
        const angles = [-30, -15, 0, 15, 30];
        angles.forEach(angle => {
          const radians = (angle * Math.PI) / 180;
          const speed = player.character.bulletSpeed || 280;
          bullets.push({
            x: centerBullet.x,
            y: centerBullet.y,
            vx: Math.sin(radians) * speed * 0.3,
            vy: -Math.cos(radians) * speed,
            damage: 1,
            friendly: true,
            projectileUrl: player.character.projectile,
            color: '#ff0000' // Red lasers
          });
        });

        return bullets;
      };
    },
    deactivate(player, gameEngine) {
      if (this.originalFire) {
        player.fire = this.originalFire;
        this.originalFire = null;
      }
    }
  },

  /**
   * One Slice (Scythe - c_6968)
   * Screen-wide horizontal flash attack for 3 seconds
   * Creates a slicing wave that damages all enemies in its path
   */
  one_slice: {
    duration: 3000,
    activate(player, gameEngine) {
      this.sliceY = player.y - 50;
      this.sliceActive = true;
      this.sliceSpeed = -400; // Move upward fast

      // Create visual effect
      this.sliceParticles = [];
      for (let i = 0; i < 20; i++) {
        this.sliceParticles.push(...createExplosion(
          Math.random() * CANVAS_WIDTH,
          this.sliceY,
          3,
          '#00ffff'
        ));
      }
      gameEngine.particles.push(...this.sliceParticles);
    },
    update(player, gameEngine, deltaTime) {
      if (!this.sliceActive) return;

      this.sliceY += (this.sliceSpeed * deltaTime) / 1000;

      // Damage all enemies at this Y position
      gameEngine.enemies.forEach(enemy => {
        if (Math.abs(enemy.y - this.sliceY) < 30) {
          const killed = enemy.takeDamage(2);
          if (killed) {
            player.addScore(enemy.type === 'boss' ? 500 : 100);
            gameEngine.particles.push(...createExplosion(enemy.x, enemy.y, 15, '#ff6b9d'));
            const idx = gameEngine.enemies.indexOf(enemy);
            if (idx > -1) gameEngine.enemies.splice(idx, 1);
          }
        }
      });

      // Deactivate if off screen
      if (this.sliceY < -50) {
        this.sliceActive = false;
      }
    },
    render(ctx, player, gameEngine) {
      if (!this.sliceActive) return;

      ctx.save();
      ctx.strokeStyle = '#00ffff';
      ctx.lineWidth = 4;
      ctx.shadowColor = '#00ffff';
      ctx.shadowBlur = 20;
      ctx.beginPath();
      ctx.moveTo(0, this.sliceY);
      ctx.lineTo(CANVAS_WIDTH, this.sliceY);
      ctx.stroke();
      ctx.restore();
    },
    deactivate(player, gameEngine) {
      this.sliceActive = false;
    }
  },

  /**
   * Orbiting Shield (Archer-Eyepatch - c_11292)
   * Orbiting projectiles that destroy enemy bullets for 10 seconds
   */
  orbiting_shield: {
    duration: 10000,
    activate(player, gameEngine) {
      this.orbitRadius = 45;
      this.orbitSpeed = 3; // radians per second
      this.projectileCount = 6;
      this.projectileSize = 12;
      this.angle = 0;

      // Create orbiting projectile positions
      this.projectiles = [];
      for (let i = 0; i < this.projectileCount; i++) {
        this.projectiles.push({
          angle: (i / this.projectileCount) * Math.PI * 2
        });
      }
    },
    update(player, gameEngine, deltaTime) {
      // Update orbit angle
      this.angle += (this.orbitSpeed * deltaTime) / 1000;

      // Update projectile positions
      this.projectiles.forEach((proj, i) => {
        const currentAngle = this.angle + (i / this.projectileCount) * Math.PI * 2;
        proj.x = player.x + Math.cos(currentAngle) * this.orbitRadius;
        proj.y = player.y + Math.sin(currentAngle) * this.orbitRadius;
      });

      // Check collision with enemy bullets
      for (let i = gameEngine.bullets.length - 1; i >= 0; i--) {
        const bullet = gameEngine.bullets[i];
        if (bullet.friendly) continue; // Skip player bullets

        // Check against each orbiting projectile
        for (let j = this.projectiles.length - 1; j >= 0; j--) {
          const proj = this.projectiles[j];
          const dx = bullet.x - proj.x;
          const dy = bullet.y - proj.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < this.projectileSize + bullet.width / 2) {
            // Destroy the enemy bullet
            gameEngine.bullets.splice(i, 1);
            // Remove the shield projectile
            this.projectiles.splice(j, 1);

            // Create small explosion effect
            gameEngine.particles.push(...createExplosion(bullet.x, bullet.y, 5, '#ffd700'));
            break;
          }
        }
      }

      // Check collision with enemies - shield projectiles destroy enemies
      for (let i = gameEngine.enemies.length - 1; i >= 0; i--) {
        const enemy = gameEngine.enemies[i];

        // Check against each orbiting projectile
        for (let j = this.projectiles.length - 1; j >= 0; j--) {
          const proj = this.projectiles[j];
          const dx = enemy.x - proj.x;
          const dy = enemy.y - proj.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < this.projectileSize + enemy.width / 2) {
            // Destroy the enemy
            const killed = enemy.takeDamage(999); // One-hit kill
            if (killed) {
              player.addScore(enemy.points || 100);
              gameEngine.particles.push(...createExplosion(enemy.x, enemy.y, 15, '#ff6b9d'));
              gameEngine.enemies.splice(i, 1);
            }

            // Remove the shield projectile
            this.projectiles.splice(j, 1);

            // Create explosion effect
            gameEngine.particles.push(...createExplosion(proj.x, proj.y, 8, '#ffd700'));
            break;
          }
        }
      }
    },
    render(ctx, player, gameEngine) {
      ctx.save();

      // Render each orbiting projectile
      this.projectiles.forEach(proj => {
        // Glow effect
        ctx.shadowColor = '#ffd700';
        ctx.shadowBlur = 10;

        // Draw projectile as glowing circle
        ctx.fillStyle = '#ffd700';
        ctx.beginPath();
        ctx.arc(proj.x, proj.y, this.projectileSize, 0, Math.PI * 2);
        ctx.fill();

        // Inner bright core
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(proj.x, proj.y, this.projectileSize * 0.5, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw orbit path (faint)
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.2)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(player.x, player.y, this.orbitRadius, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();
    },
    deactivate(player, gameEngine) {
      this.projectiles = [];
    }
  },

  /**
   * Steam Overclock (Cheetah - c_9936)
   * Rapid fire + steam particle trails for 5 seconds
   */
  steam_overclock: {
    duration: 5000,
    activate(player, gameEngine) {
      this.originalFireRate = player.fireRate;
      player.fireRate = player.fireRate * 0.3; // 3x fire rate (lower = faster)
      this.steamTimer = 0;
    },
    update(player, gameEngine, deltaTime) {
      // Create steam trail particles
      this.steamTimer += deltaTime;
      if (this.steamTimer > 50) { // Every 50ms
        gameEngine.particles.push(...createExplosion(
          player.x + (Math.random() - 0.5) * 20,
          player.y + player.height / 2,
          2,
          '#e0e0e0'
        ));
        this.steamTimer = 0;
      }
    },
    deactivate(player, gameEngine) {
      player.fireRate = this.originalFireRate;
    }
  },

  /**
   * Crema Beam (Archer-Fire - c_19431)
   * Continuous red fiery piercing beam for 5 seconds
   */
  crema_beam: {
    duration: 5000,
    activate(player, gameEngine) {
      this.beamActive = true;
      this.beamWidth = 8;
      this.damageTimer = 0;
    },
    update(player, gameEngine, deltaTime) {
      if (!this.beamActive) return;

      this.damageTimer += deltaTime;

      // Damage enemies in beam path every 100ms
      if (this.damageTimer > 100) {
        gameEngine.enemies.forEach(enemy => {
          // Check if enemy is in beam path (vertical line from player)
          if (Math.abs(enemy.x - player.x) < this.beamWidth + enemy.width / 2 &&
              enemy.y < player.y) {
            const killed = enemy.takeDamage(1);
            if (killed) {
              player.addScore(enemy.type === 'boss' ? 500 : 100);
              gameEngine.particles.push(...createExplosion(enemy.x, enemy.y, 15, '#ff4500'));
              const idx = gameEngine.enemies.indexOf(enemy);
              if (idx > -1) gameEngine.enemies.splice(idx, 1);
            }
          }
        });
        this.damageTimer = 0;
      }
    },
    render(ctx, player, gameEngine) {
      if (!this.beamActive) return;

      ctx.save();
      // Red fiery gradient for the beam
      const gradient = ctx.createLinearGradient(player.x, player.y, player.x, 0);
      gradient.addColorStop(0, '#ff4500'); // Orange-red at player
      gradient.addColorStop(0.5, '#ff0000'); // Pure red in middle
      gradient.addColorStop(1, '#ff6347'); // Tomato red at top

      ctx.fillStyle = gradient;
      ctx.shadowColor = '#ff4500';
      ctx.shadowBlur = 30;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(
        player.x - this.beamWidth / 2,
        0,
        this.beamWidth,
        player.y - player.height / 2
      );

      // Add fiery outer glow
      ctx.shadowColor = '#ff6347';
      ctx.shadowBlur = 15;
      ctx.globalAlpha = 0.5;
      ctx.fillRect(
        player.x - this.beamWidth / 2 - 2,
        0,
        this.beamWidth + 4,
        player.y - player.height / 2
      );

      ctx.restore();
    },
    deactivate(player, gameEngine) {
      this.beamActive = false;
    }
  },

  /**
   * Lupine Frenzy (Forest Wolf - c_1887)
   * Aggressive speed burst with increased movement and fire rate for 4 seconds
   */
  lupine_frenzy: {
    duration: 4000,
    activate(player, gameEngine) {
      // Store original stats
      this.originalSpeed = player.speed;
      this.originalFireRate = player.fireRate;

      // Apply boosts: +50% movement speed, +65% fire rate (lower fireRate value = faster)
      player.speed = player.speed * 1.5; // +50% speed
      player.fireRate = player.fireRate * 0.35; // +65% fire rate (multiply by 0.35 = 2.86x faster)
    },
    update(player, gameEngine, deltaTime) {
      // Create aggressive purple frenzy particles while moving
      if (Math.abs(player.vx) > 0 || Math.abs(player.vy) > 0) {
        for (let i = 0; i < 2; i++) {
          gameEngine.particles.push(...createExplosion(
            player.x + (Math.random() - 0.5) * player.width,
            player.y + (Math.random() - 0.5) * player.height,
            3,
            '#a020f0' // Purple frenzy
          ));
        }
      }
    },
    deactivate(player, gameEngine) {
      // Restore original stats
      player.speed = this.originalSpeed;
      player.fireRate = this.originalFireRate;
    }
  },

  // === MIGOS ABILITIES ===

  /**
   * 3D Glitch (White Shag - n_17763)
   * Spawn 2 hologram clones that mirror your movements for 5 seconds
   */
  '3d_glitch': {
    duration: 5000,
    activate(player, gameEngine) {
      this.clones = [
        { offsetX: -40, offsetY: 0, alpha: 0.6 },
        { offsetX: 40, offsetY: 0, alpha: 0.6 }
      ];

      // Clones fire too
      this.originalFire = player.fire.bind(player);
      player.fire = (currentTime) => {
        const bullets = [];
        const centerBullet = this.originalFire(currentTime);
        bullets.push(centerBullet);

        // Clone bullets
        this.clones.forEach(clone => {
          bullets.push({
            x: player.x + clone.offsetX,
            y: player.y + clone.offsetY,
            vx: centerBullet.vx,
            vy: centerBullet.vy,
            damage: centerBullet.damage,
            friendly: true,
            projectileUrl: player.character.projectile
          });
        });

        return bullets;
      };
    },
    render(ctx, player, gameEngine) {
      // Render clones
      this.clones.forEach(clone => {
        ctx.save();
        ctx.globalAlpha = clone.alpha;
        if (player.image && player.image.complete) {
          ctx.drawImage(
            player.image,
            player.x + clone.offsetX - player.width / 2,
            player.y + clone.offsetY - player.height / 2,
            player.width,
            player.height
          );
        }
        ctx.restore();
      });
    },
    deactivate(player, gameEngine) {
      if (this.originalFire) {
        player.fire = this.originalFire;
        this.originalFire = null;
      }
      this.clones = [];
    }
  },

  /**
   * Raid Overdrive (Pigtails - n_1826)
   * Fire rate x2 + dash ability (faster movement) for 5 seconds
   */
  raid_overdrive: {
    duration: 5000,
    activate(player, gameEngine) {
      this.originalFireRate = player.fireRate;
      this.originalSpeed = player.speed;

      player.fireRate = player.fireRate * 0.5; // 2x fire rate
      player.speed = player.speed * 1.8; // 1.8x speed
    },
    update(player, gameEngine, deltaTime) {
      // Create dash trail particles
      if (Math.abs(player.vx) > 0 || Math.abs(player.vy) > 0) {
        gameEngine.particles.push(...createExplosion(
          player.x,
          player.y,
          1,
          player.character.particleColor || '#ff69b4'
        ));
      }
    },
    deactivate(player, gameEngine) {
      player.fireRate = this.originalFireRate;
      player.speed = this.originalSpeed;
    }
  },

  /**
   * Chain Mines (Ginger - n_4269)
   * Stats doubled + temporary shield for 6 seconds
   */
  chain_mines: {
    duration: 6000,
    activate(player, gameEngine) {
      this.originalFireRate = player.fireRate;
      this.originalSpeed = player.speed;
      this.shieldActive = true;

      player.fireRate = player.fireRate * 0.5; // 2x fire rate
      player.speed = player.speed * 2; // 2x speed

      // Shield prevents damage
      this.originalTakeDamage = player.takeDamage.bind(player);
      player.takeDamage = () => false; // Invulnerable
    },
    render(ctx, player, gameEngine) {
      if (!this.shieldActive) return;

      ctx.save();
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 3;
      ctx.shadowColor = '#00ff00';
      ctx.shadowBlur = 15;
      ctx.globalAlpha = 0.7;
      ctx.beginPath();
      ctx.arc(player.x, player.y, player.width / 2 + 10, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    },
    deactivate(player, gameEngine) {
      player.fireRate = this.originalFireRate;
      player.speed = this.originalSpeed;
      player.takeDamage = this.originalTakeDamage;
      this.shieldActive = false;
    }
  },

  /**
   * Croc Shield (Croc - n_4574)
   * Invulnerability + contact damage to enemies for 4 seconds
   */
  croc_shield: {
    duration: 4000,
    activate(player, gameEngine) {
      this.shieldActive = true;

      // Invulnerability
      this.originalTakeDamage = player.takeDamage.bind(player);
      player.takeDamage = () => false;
    },
    update(player, gameEngine, deltaTime) {
      // Contact damage to enemies
      gameEngine.enemies.forEach(enemy => {
        const dx = enemy.x - player.x;
        const dy = enemy.y - player.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < (player.width + enemy.width) / 2 + 20) {
          const killed = enemy.takeDamage(3);
          if (killed) {
            player.addScore(enemy.type === 'boss' ? 500 : 100);
            gameEngine.particles.push(...createExplosion(enemy.x, enemy.y, 15, '#ff6b9d'));
            const idx = gameEngine.enemies.indexOf(enemy);
            if (idx > -1) gameEngine.enemies.splice(idx, 1);
          }
        }
      });
    },
    render(ctx, player, gameEngine) {
      if (!this.shieldActive) return;

      ctx.save();
      ctx.strokeStyle = '#32cd32';
      ctx.lineWidth = 4;
      ctx.shadowColor = '#32cd32';
      ctx.shadowBlur = 20;
      ctx.globalAlpha = 0.8;
      ctx.beginPath();
      ctx.arc(player.x, player.y, player.width / 2 + 15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    },
    deactivate(player, gameEngine) {
      player.takeDamage = this.originalTakeDamage;
      this.shieldActive = false;
    }
  },

  /**
   * Frost Flurry (Snowman - n_7220)
   * Triple-shot spread for 5 seconds
   */
  frost_flurry: {
    duration: 5000,
    activate(player, gameEngine) {
      this.originalFire = player.fire.bind(player);
      player.fire = (currentTime) => {
        const centerBullet = this.originalFire(currentTime);
        const bullets = [];

        // Create 3-way spread
        const angles = [-20, 0, 20];
        angles.forEach(angle => {
          const radians = (angle * Math.PI) / 180;
          const speed = player.character.bulletSpeed || 280;
          bullets.push({
            x: centerBullet.x,
            y: centerBullet.y,
            vx: Math.sin(radians) * speed * 0.3,
            vy: -Math.cos(radians) * speed,
            damage: 1,
            friendly: true,
            projectileUrl: player.character.projectile,
            color: '#87ceeb' // Sky blue
          });
        });

        return bullets;
      };
    },
    deactivate(player, gameEngine) {
      if (this.originalFire) {
        player.fire = this.originalFire;
        this.originalFire = null;
      }
    }
  },

  /**
   * Chomp Dash (Frog - n_8008)
   * Dash ability (increased speed) + heal on kill for 5 seconds
   */
  chomp_dash: {
    duration: 5000,
    activate(player, gameEngine) {
      this.originalSpeed = player.speed;
      player.speed = player.speed * 2.5; // 2.5x speed for dash
      this.healOnKill = true;
    },
    update(player, gameEngine, deltaTime) {
      // Heal on kill is handled by checking if special is active when enemy dies
      // We'll add this to the game engine collision logic
    },
    deactivate(player, gameEngine) {
      player.speed = this.originalSpeed;
      this.healOnKill = false;
    }
  }
};

/**
 * Get special ability by ID
 */
export function getSpecialAbility(specialId) {
  return SPECIAL_ABILITIES[specialId];
}

/**
 * Check if a special ability exists
 */
export function hasSpecialAbility(specialId) {
  return specialId in SPECIAL_ABILITIES;
}
